import React, { useEffect, useState } from "react";
import { isIOS, webOrigin } from "@/lib/nativeBridge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StorefrontIcon as Storefront, XIcon as CloseIcon, StarIcon as Star, ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";

/**
 * Days between today (UTC) and an ISO-8601 date or datetime string.
 * Mirrors `services/puppy_age_guard._parse_dob` server-side so the
 * dialog can preflight the same gate. Returns null on unparseable.
 */
function ageInDays(dobValue) {
  if (!dobValue) return null;
  const s = String(dobValue).slice(0, 10);
  const parsed = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  const utcToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.floor((utcToday - parsed.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Dialog used by the dog's owner (or admin) to list the dog for sale on
 * the marketplace, edit price/description, change sale_status, or delist.
 * Also surfaces a "Promote to top" CTA for active listings that aren't
 * currently featured.
 *
 * Props:
 *   open, onOpenChange — controlled dialog state
 *   dog                — the dog row (must include id, name, for_sale, asking_price, ...)
 *   onSaved(updated)   — fires after a successful PUT, with the updated fields
 */
export default function MarketplaceListingDialog({ open, onOpenChange, dog, onSaved }) {
  const [forSale, setForSale] = useState(false);
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [status, setStatus] = useState("available");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [featuring, setFeaturing] = useState(false);
  const [promotePromo, setPromotePromo] = useState(null);
  // Tax attestation: required server-side until the user has attested
  // once (server checks `seller_attestations`). We hydrate the
  // pre-checked state from the server so repeat listers don't see the
  // checkbox at all.
  const [taxAttest, setTaxAttest] = useState(false);
  const [alreadyAttested, setAlreadyAttested] = useState(false);
  // Per-listing seller standards acknowledgment.
  const [standardsAck, setStandardsAck] = useState(false);
  const [deviations, setDeviations] = useState("");
  const [breedRequiresAck, setBreedRequiresAck] = useState(false);
  const [hasVerifiedBadge, setHasVerifiedBadge] = useState(false);
  // Under-8-week attestation. Required server-side when the puppy's
  // DOB on the dog record puts it under 56 days as of listing time.
  // We compute the same number locally so the checkbox + microcopy
  // show up before the user hits Save (rather than only after they
  // submit and get a 400). The server is still the source of truth.
  const [under8wkAttest, setUnder8wkAttest] = useState(false);
  // Cross-border flags — added 2026-02. Both default off; sellers
  // opt in deliberately on a per-dog basis.
  const [intlShippingSupported, setIntlShippingSupported] = useState(false);
  const [breedingRightsIncluded, setBreedingRightsIncluded] = useState(false);
  const puppyAgeDays = ageInDays(dog?.dob);
  const isUnder8wk = puppyAgeDays !== null && puppyAgeDays < 56;
  const daysUntilEligible = isUnder8wk ? 56 - puppyAgeDays : 0;

  // Hydrate from the current dog when the dialog opens.
  useEffect(() => {
    if (!open || !dog) return;
    setForSale(!!dog.for_sale);
    setPrice(dog.asking_price ? String(dog.asking_price) : "");
    setCurrency(dog.sale_currency || "USD");
    setStatus(dog.sale_status || "available");
    setDescription(dog.sale_description || "");
    setTaxAttest(false);
    setUnder8wkAttest(false);
    setStandardsAck(Boolean(dog.seller_standards_listing_ack?.confirmed));
    setDeviations(dog.seller_standards_listing_ack?.deviations_disclosed || "");
    setIntlShippingSupported(Boolean(dog.intl_shipping_supported));
    setBreedingRightsIncluded(Boolean(dog.breeding_rights_included));
    api.get("/marketplace/seller-attestation")
      .then((r) => setAlreadyAttested(Boolean(r.data?.attested)))
      .catch(() => setAlreadyAttested(false));
    if (dog.breed) {
      api.get(`/seller-standards/breed-required/${encodeURIComponent(dog.breed)}`)
        .then((r) => setBreedRequiresAck(Boolean(r.data?.required)))
        .catch(() => setBreedRequiresAck(false));
    }
    api.get("/seller-standards/my-attestation")
      .then((r) => setHasVerifiedBadge(Boolean(r.data?.attested)))
      .catch(() => setHasVerifiedBadge(false));
  }, [open, dog]);

  const submit = async () => {
    if (forSale && !alreadyAttested && !taxAttest) {
      toast.error("Please confirm the seller tax attestation to list this dog.");
      return;
    }
    if (forSale && breedRequiresAck && !standardsAck) {
      toast.error("This breed requires the seller-standards acknowledgment to publish a listing.");
      return;
    }
    if (forSale && isUnder8wk && !under8wkAttest) {
      toast.error(
        `This puppy is ${puppyAgeDays} day${puppyAgeDays === 1 ? "" : "s"} old. ` +
          "Confirm you will not transfer custody before 8 weeks to list this dog.",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        for_sale: forSale,
        asking_price: forSale ? Number(price) || 0 : 0,
        sale_currency: currency || "USD",
        sale_description: description.trim(),
        sale_status: status,
        ...(forSale && !alreadyAttested ? { tax_attestation: true } : {}),
        ...(forSale && isUnder8wk ? { under_8wk_attestation: true } : {}),
        ...(forSale && standardsAck
          ? {
              seller_standards_ack: {
                confirmed: true,
                deviations_disclosed: deviations.trim().slice(0, 2000),
              },
            }
          : {}),
        intl_shipping_supported: intlShippingSupported,
        breeding_rights_included: breedingRightsIncluded,
      };
      const { data } = await api.put(`/dogs/${dog.id}/listing`, payload);
      toast.success(forSale ? "Listed on the marketplace." : "Removed from marketplace.");
      if (forSale && !alreadyAttested) setAlreadyAttested(true);
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save listing.");
    } finally {
      setSaving(false);
    }
  };

  const delist = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/dogs/${dog.id}/listing`, {
        for_sale: false,
        asking_price: 0,
        sale_description: "",
        sale_status: "available",
      });
      toast.success("Removed from marketplace.");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delist.");
    } finally {
      setSaving(false);
    }
  };

  /** Promote-to-top — same package as the sidebar Promote button.
   *  Free for admins, otherwise routes through Stripe checkout. */
  const promote = async () => {
    setFeaturing(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "marketplace_feature",
        origin_url: webOrigin(),
        dog_id: dog.id,
        promo_code: promotePromo?.valid ? promotePromo.code : undefined,
      });
      if (data.free) {
        toast.success("Promoted — listing pinned to the top for 30 days.");
        onSaved?.(data.listing ? {
          is_featured: true,
          featured_until: data.listing.featured_until,
        } : {});
        onOpenChange(false);
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFeaturing(false);
    }
  };

  // Whether this listing is currently boosted (featured + not expired).
  const isCurrentlyFeatured =
    !!dog?.is_featured &&
    !!dog?.featured_until &&
    new Date(dog.featured_until) > new Date();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="listing-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
            <Storefront size={20} weight="duotone" className="text-gold" />
            {dog?.for_sale ? "Manage marketplace listing" : "List for sale"}
          </DialogTitle>
          <DialogDescription>
            {dog?.for_sale
              ? `Update ${dog.name}'s asking price, description, or status. Buyers see this on the marketplace and on the public dog profile.`
              : `List ${dog?.name} on the marketplace so registered buyers can find and inquire about them.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={forSale}
              onChange={(e) => setForSale(e.target.checked)}
              className="mt-0.5 accent-[#C9A227]"
              data-testid="listing-for-sale"
            />
            <span>
              <span className="font-semibold">List on the marketplace</span>
              <span className="block text-[11px] text-[#6B6B6B]">
                Toggling off keeps the dog in your registry but removes the listing.
              </span>
            </span>
          </label>

          {forSale && (
            <>
              <div className="grid grid-cols-[1fr_120px] gap-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">
                    Asking price
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="50"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g. 2500"
                    className="mt-1.5"
                    data-testid="listing-price"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">
                    Currency
                  </Label>
                  <Input
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 4))}
                    placeholder="USD"
                    className="mt-1.5 uppercase"
                    data-testid="listing-currency"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">
                  Sale status
                </Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1.5" data-testid="listing-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="pending">Pending (deposit taken)</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">
                  Description
                </Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Temperament, training, health clearances — what should a buyer know?"
                  className="mt-1.5 min-h-[100px]"
                  maxLength={2000}
                  data-testid="listing-description"
                />
                <div className="text-[10px] text-[#6B6B6B] text-right mt-1">
                  {description.length}/2000
                </div>
              </div>

              <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
                Tip: high-quality photos, accurate temperament notes, and health
                clearances close sales faster than a cold price tag.
              </p>

              {/* Cross-border flags — venue-only signal to buyers; no
                  Armstrong-coordinated shipping. */}
              <div className="rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-3 space-y-2.5" data-testid="listing-intl-flags">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#3F3F3F]">
                  Cross-border options
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={intlShippingSupported}
                    onChange={(e) => setIntlShippingSupported(e.target.checked)}
                    className="mt-0.5 accent-[#C9A227] h-4 w-4"
                    data-testid="listing-intl-shipping-toggle"
                  />
                  <span className="text-[12px] leading-relaxed">
                    <strong>I'll ship internationally</strong> if the buyer
                    arranges and pays for the courier. Armstrong is venue only —
                    we don't book the transport.
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={breedingRightsIncluded}
                    onChange={(e) => setBreedingRightsIncluded(e.target.checked)}
                    className="mt-0.5 accent-[#C9A227] h-4 w-4"
                    data-testid="listing-breeding-rights-toggle"
                  />
                  <span className="text-[12px] leading-relaxed">
                    <strong>Sold with full breeding rights</strong> (not limited /
                    pet-only papers). Adds a "Breeding rights" badge on the card
                    so foundation-stock buyers can find this listing.
                  </span>
                </label>
              </div>

              {/* Seller standards — strongly encouraged. Required for breeds
                  the admin has flagged (e.g. snub-nosed, working breeds). */}
              {forSale && (
                <div
                  className={`rounded-xl border p-3.5 mt-2 ${
                    breedRequiresAck
                      ? "bg-[#FFF8E5] border-[#C9A227]"
                      : "bg-[#F1FAF3] border-[#0F8043]/30"
                  }`}
                  data-testid="seller-standards-ack-card"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <ShieldCheck size={16} weight="fill" className={breedRequiresAck ? "text-[#C9A227] mt-0.5" : "text-[#0F8043] mt-0.5"} />
                    <div className="flex-1">
                      <p className="text-[12px] font-semibold text-ink">
                        {breedRequiresAck
                          ? "Seller-standards acknowledgment is required for this breed"
                          : "Seller-standards acknowledgment (strongly encouraged)"}
                      </p>
                      <p className="text-[11px] text-[#3F3F3F] leading-relaxed mt-0.5">
                        Confirm your listing description discloses any deviation
                        from Armstrong's <a href="/seller-standards" target="_blank" rel="noopener noreferrer" className="underline font-medium">15-item seller standards</a>{" "}
                        (health screening, vaccinations, microchip, written
                        guarantee, etc.). Listings backed by an attestation
                        sell faster and dispute less.
                      </p>
                    </div>
                  </div>
                  <label
                    className="flex items-start gap-2 mt-1 cursor-pointer"
                    data-testid="seller-standards-ack-label"
                  >
                    <input
                      type="checkbox"
                      checked={standardsAck}
                      onChange={(e) => setStandardsAck(e.target.checked)}
                      className="mt-0.5 accent-[#0F8043] h-4 w-4"
                      data-testid="seller-standards-ack-checkbox"
                    />
                    <span className="text-[12px] text-ink leading-relaxed">
                      <strong>I confirm</strong> this dog meets the platform's
                      seller standards <em>or</em> any deviations are disclosed
                      below in plain English.
                    </span>
                  </label>
                  {standardsAck && (
                    <Textarea
                      value={deviations}
                      onChange={(e) => setDeviations(e.target.value)}
                      placeholder="Optional · Disclose any standards items not met (e.g., 'OFA hips not yet completed; will provide before transfer')."
                      className="mt-2 min-h-[60px] text-[12px]"
                      maxLength={2000}
                      data-testid="seller-standards-deviations"
                    />
                  )}
                  {!hasVerifiedBadge && standardsAck && (
                    <a
                      href="/seller-standards/attest"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-[#8A6A00] underline mt-2 inline-block"
                      data-testid="earn-badge-link"
                    >
                      → Earn the public Verified Standards badge for your kennel
                    </a>
                  )}
                </div>
              )}

              {/* Seller tax attestation — required once per seller. */}
              {forSale && !alreadyAttested && (
                <label
                  className="flex items-start gap-2 mt-2 rounded-lg border border-[#E8D9A6] bg-[#FBF7EC] px-3 py-2.5 text-[11px] cursor-pointer text-[#8A6A00] leading-relaxed"
                  data-testid="seller-tax-attestation"
                >
                  <input
                    type="checkbox"
                    checked={taxAttest}
                    onChange={(e) => setTaxAttest(e.target.checked)}
                    className="mt-0.5 accent-[#C9A227]"
                    data-testid="seller-tax-attestation-checkbox"
                  />
                  <span>
                    <strong>I am the merchant of record for this sale.</strong>{" "}
                    I will collect and remit any applicable sales tax in my
                    nexus state, file my own 1099-K (issued directly to me by Stripe),
                    and Armstrong K9 Registry is acting only as a
                    payment-routing venue, not the seller. (One-time confirmation —
                    you won't see this again.)
                  </span>
                </label>
              )}
              {forSale && alreadyAttested && (
                <div
                  className="text-[11px] text-[#0F8043] flex items-center gap-1 mt-1"
                  data-testid="seller-tax-attestation-acked"
                >
                  ✓ Tax responsibility attested · You are the merchant of record.
                </div>
              )}

              {/* Under-8-week attestation. Triggers whenever the puppy
                  is < 56 days old as of listing time. Money cannot
                  move on this listing until the puppy ages in — escrow
                  Checkout has a separate hard-block — so this panel is
                  purely the "ok to publish ahead of eligibility" gate. */}
              {forSale && isUnder8wk && (
                <label
                  className="flex items-start gap-2 mt-2 rounded-lg border border-[#C1272D]/40 bg-[#FFF4F4] px-3 py-2.5 text-[11.5px] cursor-pointer text-[#A4222A] leading-relaxed"
                  data-testid="under-8wk-attestation"
                >
                  <input
                    type="checkbox"
                    checked={under8wkAttest}
                    onChange={(e) => setUnder8wkAttest(e.target.checked)}
                    className="mt-0.5 accent-[#C1272D]"
                    data-testid="under-8wk-attestation-checkbox"
                  />
                  <span>
                    <strong>
                      This puppy is {puppyAgeDays} day{puppyAgeDays === 1 ? "" : "s"} old —
                      under the 8-week (56-day) statutory minimum transfer age.
                    </strong>{" "}
                    Many U.S. states (CA, NY, IL, VA, MD, +) prohibit transferring
                    a puppy under 8 weeks. I confirm I will NOT release custody to a
                    buyer before this puppy reaches 8 weeks of age
                    ({daysUntilEligible} day{daysUntilEligible === 1 ? "" : "s"} from today).
                    Listing publishes now to start a waitlist; the Buy button stays
                    disabled until the puppy ages in.
                  </span>
                </label>
              )}

              {/* Promote-to-top upsell — only visible for active listings */}
              {dog?.for_sale && (
                <div
                  className={`mt-2 rounded-xl border p-4 flex items-start gap-3 ${
                    isCurrentlyFeatured
                      ? "bg-gradient-to-br from-[#FFF7E0] via-white to-[#FBF7EC] border-[#C9A227]"
                      : "bg-[#FBF7EC]/50 border-[#C9A227]/40"
                  }`}
                  data-testid="listing-promote-card"
                >
                  <div className="h-9 w-9 rounded-lg bg-white border border-[#C9A227]/40 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Star
                      size={16}
                      weight={isCurrentlyFeatured ? "fill" : "duotone"}
                      className="text-gold"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                      {isCurrentlyFeatured ? (
                        <>
                          Featured listing
                          <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#C9A227] text-white rounded-full px-1.5 py-0.5">
                            Live
                          </span>
                        </>
                      ) : (
                        <>
                          Promote to the top of search
                          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8A6A00]">
                            $29 · 30 days
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-[11px] text-[#6B6B6B] mt-1 leading-relaxed">
                      {isCurrentlyFeatured
                        ? `Your listing is pinned at the top of every marketplace search until ${new Date(
                            dog.featured_until
                          ).toLocaleDateString(undefined, {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}.`
                        : "Pinned listings get 5–10× the visibility — faster inquiries, faster sales."}
                    </p>
                    {!isCurrentlyFeatured && (
                      <>
                        <div className="mt-3">
                          <CompBanner className="mb-3" />
                          <PromoCodeInput
                            kind="marketplace_feature"
                            amount={29}
                            applied={promotePromo}
                            onApplied={(p) => {
                              setPromotePromo(p);
                              toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
                            }}
                            onCleared={() => setPromotePromo(null)}
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={promote}
                          disabled={featuring || saving}
                          className={`rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white mt-3 h-8 text-xs ${isIOS() ? "hidden" : ""}`}
                          data-testid="listing-promote-btn"
                        >
                          <Star size={12} weight="duotone" className="mr-1" />
                          {featuring
                            ? "Processing…"
                            : promotePromo?.valid
                            ? `Promote · $${promotePromo.final.toFixed(2)}`
                            : "Promote · $29"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {dog?.for_sale && (
            <Button
              variant="outline"
              onClick={delist}
              disabled={saving}
              className="rounded-full border-[#C1272D]/30 text-crimson hover:bg-[#C1272D]/5"
              data-testid="listing-delist"
            >
              <CloseIcon size={14} className="mr-1" /> Remove listing
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || (forSale && !alreadyAttested && !taxAttest) || (forSale && isUnder8wk && !under8wkAttest)}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="listing-save"
          >
            {saving ? "Saving…" : forSale ? "Save listing" : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
